import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { focusRing } from '@heidi/design-system/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@heidi/design-system/table';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight } from 'lucide-react';

import { InfoChart } from './InfoChart';
import type { InfoBlock, InfoFactTone } from './workspace.types';

/**
 * Renderers for the typed blocks of a workspace information card. Read-only
 * by design — the only interaction is expanding `details` sections; deeper
 * drill-down happens conversationally via the card's follow-up chips.
 */

const FACT_VALUE_TONE: Record<InfoFactTone, string> = {
  default: 'text-text-primary',
  positive: 'text-green-700',
  warning: 'text-validation-warning',
  critical: 'text-validation-destructive',
};

const FACT_DOT_TONE: Record<Exclude<InfoFactTone, 'default'>, string> = {
  positive: 'bg-green-600',
  warning: 'bg-validation-warning',
  critical: 'bg-validation-destructive',
};

function BlockTitle({ title }: { title?: string }) {
  if (!title) return null;
  return (
    <TypographyCaptionBold className="text-text-secondary">{title}</TypographyCaptionBold>
  );
}

const chartAriaLabel = (block: Extract<InfoBlock, { type: 'chart' }>): string => {
  const points = block.series
    .map((point) => `${point.label} ${point.value}${block.unit ?? ''}`)
    .join(', ');
  return `${block.title ?? 'Chart'}: ${points}`;
};

export function InfoBlockView({ block }: { block: InfoBlock }) {
  switch (block.type) {
    case 'text':
      return (
        <div className="flex flex-col gap-1.5">
          <BlockTitle title={block.title} />
          <TypographyP2 className="whitespace-pre-wrap text-pretty text-text-primary">
            {block.body}
          </TypographyP2>
        </div>
      );

    case 'facts':
      return (
        <div className="flex flex-col gap-2">
          <BlockTitle title={block.title} />
          <dl className="grid grid-cols-2 gap-2 lg:grid-cols-3">
            {block.facts.map((fact, index) => (
              <div
                key={`${fact.label}-${index}`}
                className="flex min-w-0 flex-col gap-0.5 rounded-xl border border-border/60 bg-background-secondary/40 px-3 py-2.5"
              >
                <dt>
                  <TypographyCaption className="truncate text-text-tertiary">
                    {fact.label}
                  </TypographyCaption>
                </dt>
                <dd
                  className={cn(
                    'flex items-baseline gap-1.5 text-base font-semibold tabular-nums',
                    FACT_VALUE_TONE[fact.tone ?? 'default']
                  )}
                >
                  {fact.tone && fact.tone !== 'default' ? (
                    <span
                      className={cn(
                        'size-1.5 shrink-0 self-center rounded-full',
                        FACT_DOT_TONE[fact.tone]
                      )}
                      aria-hidden
                    />
                  ) : null}
                  <span className="min-w-0 text-pretty">{fact.value}</span>
                </dd>
                {fact.hint ? (
                  <TypographyCaption className="text-pretty text-text-tertiary">
                    {fact.hint}
                  </TypographyCaption>
                ) : null}
              </div>
            ))}
          </dl>
        </div>
      );

    case 'table':
      return (
        <div className="flex flex-col gap-2">
          <BlockTitle title={block.title} />
          <Table wrapperClassName="rounded-xl border border-border/60">
            <TableHeader>
              <TableRow>
                {block.columns.map((column, index) => (
                  <TableHead key={`${column}-${index}`}>{column}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {block.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <TableCell key={cellIndex} className="tabular-nums">
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );

    case 'chart':
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-1.5">
            <BlockTitle title={block.title} />
            {block.unit ? (
              <TypographyCaption className="text-text-tertiary">
                ({block.unit})
              </TypographyCaption>
            ) : null}
          </div>
          <div className="rounded-xl border border-border/60 bg-background-secondary/30 px-2 pb-1 pt-3">
            <InfoChart
              chartKind={block.chartKind}
              series={block.series}
              unit={block.unit}
              referenceLow={block.referenceLow}
              referenceHigh={block.referenceHigh}
              referenceLabel={block.referenceLabel}
              ariaLabel={chartAriaLabel(block)}
            />
          </div>
        </div>
      );

    case 'list': {
      const ListTag = block.listStyle === 'numbered' ? 'ol' : 'ul';
      return (
        <div className="flex flex-col gap-2">
          <BlockTitle title={block.title} />
          <ListTag className="flex flex-col gap-2">
            {block.items.map((item, index) => (
              <li key={index} className="flex gap-2.5">
                {block.listStyle === 'numbered' ? (
                  <span
                    className="w-4 shrink-0 text-end text-sm tabular-nums text-text-tertiary"
                    aria-hidden
                  >
                    {index + 1}.
                  </span>
                ) : (
                  <span
                    className="mt-[0.5em] size-1.5 shrink-0 rounded-full bg-text-tertiary"
                    aria-hidden
                  />
                )}
                <span className="flex min-w-0 flex-col gap-0.5">
                  <TypographyP2 className="text-pretty text-text-primary">
                    {item.text}
                  </TypographyP2>
                  {item.detail ? (
                    <TypographyCaption className="text-pretty text-text-secondary">
                      {item.detail}
                    </TypographyCaption>
                  ) : null}
                </span>
              </li>
            ))}
          </ListTag>
        </div>
      );
    }

    case 'timeline':
      return (
        <div className="flex flex-col gap-2">
          <BlockTitle title={block.title} />
          <ol className="flex flex-col">
            {block.events.map((event, index) => (
              <li
                key={`${event.date}-${index}`}
                className="relative flex gap-3 pb-4 ps-5 last:pb-0"
              >
                {index < block.events.length - 1 ? (
                  <span
                    className="absolute start-[4.5px] top-3 h-full w-px bg-border"
                    aria-hidden
                  />
                ) : null}
                <span
                  className="absolute start-0 top-[5px] size-2.5 rounded-full border-2 border-surface bg-text-tertiary"
                  aria-hidden
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <TypographyCaption className="tabular-nums text-text-tertiary">
                    {event.date}
                  </TypographyCaption>
                  <TypographyP2 className="text-pretty text-text-primary">
                    {event.text}
                  </TypographyP2>
                  {event.detail ? (
                    <TypographyCaption className="text-pretty text-text-secondary">
                      {event.detail}
                    </TypographyCaption>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </div>
      );

    case 'details':
      return (
        <Collapsible>
          <CollapsibleTrigger
            className={cn(
              'group flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-start',
              'hover:bg-surface-hover',
              focusRing
            )}
          >
            <ChevronRight
              className="size-4 shrink-0 text-text-tertiary transition-transform duration-150 group-data-[state=open]:rotate-90 motion-reduce:transition-none"
              aria-hidden
            />
            <TypographyCaptionBold className="text-pretty text-text-secondary">
              {block.title}
            </TypographyCaptionBold>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-3 pb-1 pe-2 ps-7 pt-2">
              {block.blocks.map((inner, index) => (
                <InfoBlockView key={index} block={inner} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );

    default: {
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}
